import {
  Button,
  CloseIcon,
  DatePicker,
  Drawer,
  InputNumber,
  Radio,
  SelectSearch,
  Text,
} from '@mercai/clever'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DECIMAL_PLACES_DEFAULT } from '@/constants'
import { useSupplierList } from '@/services'
import { SupplierForecastAgreementEnum } from '@/types'

import { DrawerFilterProps } from './drawer-supplier-forecast-filters.type'

import styles from './drawer-supplier-forecast-filters.module.css'

export const DrawerSupplierForecastFilter = ({
  isOpen,
  onHandleOpen,
  filter,
  onHandleFilter,
}: DrawerFilterProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSuppliers')

  const [costExpirationBefore, setCostExpirationBefore] = useState(
    filter?.expiresAtBefore || '',
  )
  const [costExpirationAfter, setCostExpirationAfter] = useState(
    filter?.expiresAtAfter || '',
  )
  const [usersSearchTerm, setUsersSearchTerm] = useState<string>('')
  const [selectedSupplier, setSelectedSupplier] = useState<
    | {
      label: string
      value: string
    }
    | undefined
  >(
    filter?.supplierProduct_Supplier_Name
      ? {
        label: filter?.supplierProduct_Supplier_Name,
        value: filter?.supplierProduct_Supplier_Name,
      }
      : undefined,
  )

  const { data: dataSuppliers, isLoading: isLoadingSuppliersSearch } =
    useSupplierList({
      params: {
        limit: 100,
        page: 1,
        isStaff: 'true',
        search: usersSearchTerm,
      },
    })

  const getBooleanOfSingleValue = (key: string, value: string): boolean => {
    return filter[key] === value
  }

  const onChangeBooleanOfSingleValue = (key: string, value: string) => {
    onHandleFilter({ ...filter, [key]: value })
  }

  const onApplyDate = (key: string) => {
    const value =
      key === 'expiresAtBefore'
        ? costExpirationBefore
          ? `${costExpirationBefore}T20:59:59`
          : ''
        : costExpirationAfter
          ? `${costExpirationAfter}T00:00:00`
          : ''

    onHandleFilter({
      ...filter,
      [key]: !value ? '' : value,
    })
  }

  const supplierOptions = useMemo(() => {
    const suppliersWithName =
      dataSuppliers?.data?.results?.filter((supplier) => supplier?.name) || []

    const mappedSupliers = suppliersWithName.map((supplier) => ({
      label: supplier.name,
      value: `${supplier.name}`,
    }))

    const options = [
      { label: t('supplierProductsFilterSelectEmptyLabel'), value: '' },
      ...mappedSupliers,
    ]

    return options.sort((a, b) => a.label.localeCompare(b.label))
  }, [dataSuppliers, t])

  useEffect(() => {
    if (isOpen) {
      setCostExpirationBefore(filter?.expiresAtBefore || '')
      setCostExpirationAfter(filter?.expiresAtAfter || '')
      setSelectedSupplier(
        filter?.supplierProduct_Supplier_Name
          ? {
            label: filter?.supplierProduct_Supplier_Name,
            value: filter?.supplierProduct_Supplier_Name,
          }
          : undefined,
      )
    } else {
      setCostExpirationBefore('')
      setCostExpirationAfter('')
      setSelectedSupplier(undefined)
      setUsersSearchTerm('')
    }
  }, [isOpen])

  return (
    <Drawer isOpen={isOpen} onHandleOpen={onHandleOpen}>
      <div className={styles.header}>
        <Text variant="title-large" className={styles['header-title']}>
          {t('supplierProductsFilterTitle')}
        </Text>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('supplierProductsFilterCostExpirationDateLabel')}
          </Text>

          <div className={styles['content-filter-row']}>
            <DatePicker
              value={costExpirationAfter}
              onChange={(value) => setCostExpirationAfter(value as string)}
              placeholder="dd/mm/aaaa"
              onBlur={() => onApplyDate('expiresAtAfter')}
            />

            <Text>-</Text>

            <DatePicker
              value={costExpirationBefore}
              onChange={(value) => setCostExpirationBefore(value as string)}
              placeholder="dd/mm/aaaa"
              onBlur={() => onApplyDate('expiresAtBefore')}
            />
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('supplierProductsFilterSupplierLabel')}
          </Text>
          <SelectSearch<{ label: string; value: string }>
            dataSearch={supplierOptions}
            isLoadingSearch={isLoadingSuppliersSearch}
            onSearch={setUsersSearchTerm}
            onSelectItem={({ label = '', value = '' }) => {
              setSelectedSupplier({ label, value })
              onHandleFilter({
                ...filter,
                supplierProduct_Supplier_Name: value,
              })
            }}
            selectedItem={selectedSupplier}
            translations={{
              inputSearchPlaceholder: t(
                'supplierProductsFilterSupplierPlaceholderLabel',
              ),
              noResults: t('supplierProductsFilterSupplierNotfoundLabel'),
            }}
          />
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('supplierProductsFilterAgreementLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            {Object.keys(SupplierForecastAgreementEnum).map((key) => (
              <Radio
                key={key}
                value={getBooleanOfSingleValue('agreementType', key)}
                onChange={() =>
                  onChangeBooleanOfSingleValue('agreementType', key)
                }
              >
                {tEnums(`forecast_agreement_${key}`)}
              </Radio>
            ))}

            <Radio
              value={getBooleanOfSingleValue('agreementType', '')}
              onChange={() => onChangeBooleanOfSingleValue('agreementType', '')}
            >
              {t('supplierProductsFilterAllLabel')}
            </Radio>
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('supplierProductsFilterQuantityLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            <InputNumber
              showIcons
              value={filter.quantityMin === '' ? undefined : filter.quantityMin}
              onChange={(newValue) => {
                onHandleFilter({
                  ...filter,
                  quantityMin: Number(newValue) > 0 ? `${newValue}` : '',
                })
              }}
              placeholder={t('supplierProductsFilterMinPlaceholder')}
              decimalPrecision={0}
            />

            <InputNumber
              showIcons
              value={filter.quantityMax === '' ? undefined : filter.quantityMax}
              onChange={(newValue) => {
                onHandleFilter({
                  ...filter,
                  quantityMax: Number(newValue) > 0 ? `${newValue}` : '',
                })
              }}
              placeholder={t('supplierProductsFilterMaxPlaceholder')}
              decimalPrecision={0}
            />
          </div>
        </div>

        <div className={styles['content-filter']}>
          <Text variant="title-base">
            {t('supplierProductsFilterCostLabel')}
          </Text>

          <div className={styles['content-filter-column']}>
            <InputNumber
              showIcons
              value={filter.costMin === '' ? undefined : filter.costMin}
              onChange={(newValue) => {
                onHandleFilter({
                  ...filter,
                  costMin: Number(newValue) > 0 ? `${newValue}` : '',
                })
              }}
              placeholder={t('supplierProductsFilterMinPlaceholder')}
              decimalPrecision={DECIMAL_PLACES_DEFAULT}
            />

            <InputNumber
              showIcons
              value={filter.costMax === '' ? undefined : filter.costMax}
              onChange={(newValue) => {
                onHandleFilter({
                  ...filter,
                  costMax: Number(newValue) > 0 ? `${newValue}` : '',
                })
              }}
              placeholder={t('supplierProductsFilterMaxPlaceholder')}
              decimalPrecision={DECIMAL_PLACES_DEFAULT}
            />
          </div>
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onHandleOpen}>
          {t('backButton')}
        </Button>
      </div>
    </Drawer>
  )
}
